export type TakeoverStreamFramePart = {
  kind: 'frame';
  requestId: string | null;
  seq: number;
  capturedAt: string | null;
  publishedAt: string | null;
  contentType: 'image/jpeg';
  bytes: Uint8Array<ArrayBufferLike>;
};

export type TakeoverStreamHeartbeatPart = {
  kind: 'heartbeat';
  requestId: string | null;
  seq: number | null;
  publishedAt: string | null;
};

export type TakeoverStreamPart =
  TakeoverStreamFramePart | TakeoverStreamHeartbeatPart;

type ParserState =
  | { kind: 'boundary' }
  | { kind: 'headers' }
  | {
      kind: 'body';
      headers: Record<string, string>;
      contentLength: number;
    };

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const CRLF = encoder.encode('\r\n');
const HEADER_END = encoder.encode('\r\n\r\n');
const MAX_HEADER_BYTES = 16 * 1024;
const MAX_BODY_BYTES = 5 * 1024 * 1024;

export function boundaryFromContentType(contentType: string | null): string {
  const match = /(?:^|;)\s*boundary=(?:"([^"]+)"|([^;]+))/i.exec(
    contentType ?? '',
  );
  return (match?.[1] ?? match?.[2] ?? '').trim();
}

export class TakeoverMultipartParser {
  private readonly boundary: Uint8Array<ArrayBufferLike>;
  private readonly boundaryMarker: Uint8Array<ArrayBufferLike>;
  private buffer: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
  private start = 0;
  private end = 0;
  private state: ParserState = { kind: 'boundary' };

  constructor(boundary: string) {
    if (!boundary.trim()) throw new Error('Missing multipart boundary');
    this.boundary = encoder.encode(boundary);
    this.boundaryMarker = encoder.encode(`--${boundary}`);
  }

  push(chunk: Uint8Array<ArrayBufferLike>): TakeoverStreamPart[] {
    if (this.length + chunk.length > MAX_BODY_BYTES + MAX_HEADER_BYTES) {
      throw new Error('Takeover stream part exceeded the byte limit');
    }
    this.append(chunk);
    return this.drain(false);
  }

  finish(): TakeoverStreamPart[] {
    return this.drain(true);
  }

  private drain(final: boolean): TakeoverStreamPart[] {
    const parts: TakeoverStreamPart[] = [];
    for (;;) {
      if (this.state.kind === 'boundary') {
        const available = this.available();
        const index = indexOf(available, this.boundaryMarker);
        if (index < 0) {
          if (final) this.clear();
          else this.keepTail(this.boundaryMarker.length);
          return parts;
        }
        this.consume(index + this.boundaryMarker.length);
        if (startsWith(this.available(), encoder.encode('--'))) {
          this.clear();
          return parts;
        }
        this.consumePrefix(CRLF);
        this.state = { kind: 'headers' };
      }

      if (this.state.kind === 'headers') {
        const available = this.available();
        const end = indexOf(available, HEADER_END);
        if (end < 0 && available.length > MAX_HEADER_BYTES) {
          throw new Error('Takeover stream headers exceeded the byte limit');
        }
        if (end < 0) return parts;
        const headers = parseHeaders(decoder.decode(this.read(end)));
        const contentLength = Number(headers['content-length']);
        if (contentLength > MAX_BODY_BYTES) {
          throw new Error('Takeover stream body exceeded the byte limit');
        }
        this.consume(end + HEADER_END.length);
        if (!Number.isSafeInteger(contentLength) || contentLength < 0) {
          this.state = { kind: 'boundary' };
          continue;
        }
        this.state = { kind: 'body', headers, contentLength };
      }

      if (this.state.kind === 'body') {
        if (this.length < this.state.contentLength) return parts;
        const body = this.read(this.state.contentLength);
        const parsed = partFromBody(this.state.headers, body);
        if (parsed) parts.push(parsed);
        this.consume(this.state.contentLength);
        this.consumePrefix(CRLF);
        this.state = { kind: 'boundary' };
      }
    }
  }

  private get length(): number {
    return this.end - this.start;
  }

  private available(): Uint8Array<ArrayBufferLike> {
    return this.buffer.subarray(this.start, this.end);
  }

  private read(length: number): Uint8Array<ArrayBufferLike> {
    return this.buffer.slice(this.start, this.start + length);
  }

  private consume(length: number): void {
    this.start += length;
    if (this.start === this.end) this.clear();
  }

  private consumePrefix(prefix: Uint8Array<ArrayBufferLike>): void {
    if (startsWith(this.available(), prefix)) this.consume(prefix.length);
  }

  private append(chunk: Uint8Array<ArrayBufferLike>): void {
    if (chunk.length === 0) return;
    this.ensureCapacity(chunk.length);
    this.buffer.set(chunk, this.end);
    this.end += chunk.length;
  }

  private ensureCapacity(extraBytes: number): void {
    const required = this.length + extraBytes;
    if (this.buffer.length >= this.end + extraBytes) return;
    if (this.buffer.length >= required) {
      this.buffer.copyWithin(0, this.start, this.end);
      this.end = this.length;
      this.start = 0;
      return;
    }
    let capacity = Math.max(this.buffer.length * 2, required, 1024);
    while (capacity < required) capacity *= 2;
    const next = new Uint8Array(capacity);
    next.set(this.available(), 0);
    this.end = this.length;
    this.start = 0;
    this.buffer = next;
  }

  private keepTail(maxLength: number): void {
    if (this.length <= maxLength) return;
    const tail = this.buffer.slice(this.end - maxLength, this.end);
    this.buffer.set(tail, 0);
    this.start = 0;
    this.end = tail.length;
  }

  private clear(): void {
    this.start = 0;
    this.end = 0;
  }
}

function partFromBody(
  headers: Record<string, string>,
  body: Uint8Array<ArrayBufferLike>,
): TakeoverStreamPart | null {
  const kind =
    headers['x-takeover-part'] ??
    headers['x-takeover-kind'] ??
    headers['x-part-kind'];
  if (kind === 'heartbeat') {
    const text = decoder.decode(body).trim();
    const json = text ? safeJson(text) : null;
    const seq = Number(headers['x-takeover-seq'] ?? json?.seq);
    return {
      kind: 'heartbeat',
      requestId: stringOrNull(
        headers['x-takeover-request-id'] ??
          headers['x-request-id'] ??
          json?.requestId,
      ),
      seq: Number.isSafeInteger(seq) && seq >= 0 ? seq : null,
      publishedAt: stringOrNull(
        headers['x-takeover-published-at'] ??
          headers['x-published-at'] ??
          json?.publishedAt,
      ),
    };
  }
  if (kind && kind !== 'frame') return null;
  const contentType = (headers['content-type'] ?? '').split(';')[0] ?? '';
  const normalizedContentType = contentType.trim().toLowerCase();
  if (normalizedContentType !== 'image/jpeg') {
    throw new Error('Takeover stream frame content type was not image/jpeg');
  }
  const seq = Number(
    headers['x-takeover-seq'] ?? headers['x-frame-seq'] ?? headers['x-seq'],
  );
  if (!Number.isSafeInteger(seq) || seq < 0) return null;
  return {
    kind: 'frame',
    requestId: stringOrNull(
      headers['x-takeover-request-id'] ?? headers['x-request-id'],
    ),
    seq,
    capturedAt: stringOrNull(
      headers['x-takeover-captured-at'] ?? headers['x-captured-at'],
    ),
    publishedAt: stringOrNull(
      headers['x-takeover-published-at'] ?? headers['x-published-at'],
    ),
    contentType: 'image/jpeg',
    bytes: body,
  };
}

function safeJson(text: string): Record<string, unknown> | null {
  try {
    const value = JSON.parse(text) as unknown;
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function parseHeaders(raw: string): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const line of raw.split(/\r\n/)) {
    const index = line.indexOf(':');
    if (index <= 0) continue;
    headers[line.slice(0, index).trim().toLowerCase()] = line
      .slice(index + 1)
      .trim();
  }
  return headers;
}

function indexOf(
  haystack: Uint8Array<ArrayBufferLike>,
  needle: Uint8Array<ArrayBufferLike>,
): number {
  if (needle.length === 0) return 0;
  outer: for (let i = 0; i <= haystack.length - needle.length; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

function startsWith(
  haystack: Uint8Array<ArrayBufferLike>,
  needle: Uint8Array<ArrayBufferLike>,
): boolean {
  if (haystack.length < needle.length) return false;
  for (let index = 0; index < needle.length; index += 1) {
    if (haystack[index] !== needle[index]) return false;
  }
  return true;
}
